// src/config/bookSizes.js
//
// The trim sizes a book can be printed at — one list, for every place that has an
// opinion about how big the book is.
//
// **Why this exists.** The size was being chosen in three places that never spoke
// to each other:
//
//   * the quote estimator (`backend/routes/quotes.py`), where the author picks a
//     size and is priced on it;
//   * the cover designer (`BOOK_SIZES` in `TextStyleToolbar.jsx`), where the cover
//     is drawn at a trim size;
//   * the manuscript editor, which offered Letter / A4 / Legal / Tabloid — office
//     paper sizes, not one of which is a book.
//
// So an author could be quoted for 6 × 9, have a cover designed at 6 × 9, and
// write and export a manuscript at 8.5 × 11. A cover and an interior that are not
// the same size cannot be bound into one book.
//
// `aliases` maps the ids those two callers already store, so nothing has to be
// migrated and no existing quote changes price. **The quote ids are load-bearing**
// — they are what `print_spec.selections.size` holds on drafts that already exist,
// and `quotes.py` prices against them. Rename one and you reprice someone's book.
//
// Mirrored for the backend in `backend/utils/book_sizes.py`, which cannot import
// this: CRA refuses imports from outside `src/`, and the Docker build context is
// `backend/` alone. `backend/tests/test_book_sizes.py` fails if the two drift.

// 96 CSS pixels per inch — the same basis as the editor's 816 × 1056 Letter sheet.
export const PX_PER_INCH = 96;

export const BOOK_SIZES = [
  {
    id: 'quarter_letter',
    label: '1/4 Letter',
    widthIn: 4.25,
    heightIn: 5.5,
    aliases: { quote: 'quarter_letter' },
  },
  {
    id: 'pocket',
    label: 'Pocket / Mass Market',
    widthIn: 4.25,
    heightIn: 6.875,
    aliases: { cover: 'pocket_4x7' },
  },
  {
    id: 'half_letter',
    label: '1/2 Letter',
    widthIn: 5.5,
    heightIn: 8.5,
    aliases: { quote: 'half_letter', cover: 'half_letter' },
  },
  {
    id: 'a5',
    label: 'A5',
    widthIn: 5.83,
    heightIn: 8.27,
    aliases: { cover: 'a5' },
  },
  {
    id: 'trade_6x9',
    label: 'Trade',
    widthIn: 6,
    heightIn: 9,
    aliases: { quote: '6x9', cover: 'trade_6x9' },
  },
  {
    id: 'us_letter',
    label: 'US Letter',
    widthIn: 8.5,
    heightIn: 11,
    aliases: { quote: 'letter', cover: 'us_letter' },
  },
];

/**
 * What a draft is printed at when nothing says otherwise.
 *
 * US Letter, deliberately — it is what every existing manuscript has been written
 * and paginated at. A different default would silently re-flow books that were
 * finished months ago. Authors choose a real trim size; they are not given one.
 */
export const DEFAULT_BOOK_SIZE = 'us_letter';

export const BOOK_SIZES_BY_ID = BOOK_SIZES.reduce((acc, size) => {
  acc[size.id] = size;
  return acc;
}, {});

/** A size by id, falling back rather than returning undefined. */
export function bookSize(id) {
  return BOOK_SIZES_BY_ID[id] || BOOK_SIZES_BY_ID[DEFAULT_BOOK_SIZE];
}

/** `'6 × 9 in'` — for a dropdown label beside the name. */
export function dimensionsLabel(id) {
  const size = bookSize(id);
  return `${size.widthIn} × ${size.heightIn} in`;
}

/** The page box in CSS pixels, which is what the editor's pagination works in. */
export function pageSizePx(id) {
  const size = bookSize(id);
  return {
    width: Math.round(size.widthIn * PX_PER_INCH),
    height: Math.round(size.heightIn * PX_PER_INCH),
  };
}

/**
 * The trim size a quote's selection means.
 *
 * Returns null for `custom`, which carries no dimensions — the caller decides
 * what to do rather than being handed a wrong size.
 */
export function fromQuoteSelection(quoteId) {
  if (!quoteId || quoteId === 'custom') return null;
  const match = BOOK_SIZES.find((s) => s.aliases.quote === quoteId);
  return match ? match.id : null;
}

/** The trim size a cover version's `bookSize` key means. */
export function fromCoverKey(coverKey) {
  if (!coverKey) return null;
  const match = BOOK_SIZES.find((s) => s.aliases.cover === coverKey);
  return match ? match.id : null;
}

/** The cover designer's key for a trim size, so a cover can be set to match. */
export function toCoverKey(id) {
  return bookSize(id).aliases.cover || null;
}
